/** Barra persistente della chiamata: resta visibile navigando fra le pagine.
 *
 * Serve al caso d'uso principale del supporto: il supervisore condivide lo schermo
 * e naviga nel portale per mostrare come si usa. Prima la chiamata restava viva
 * (il provider sta in MainLayout) ma video e controlli sparivano insieme alla
 * pagina /supporto, e sembrava di essere usciti dalla conferenza.
 */
import { Maximize2, Mic, MicOff, Monitor, MonitorOff, PhoneOff, Video, VideoOff } from "lucide-react";
import { useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

import { useSupport } from "./SupportContext";

/** Icona sola: 44x44 come minimo tocco, con etichetta per gli screen reader. */
const iconButton =
  "flex h-11 w-11 items-center justify-center rounded-lg bg-surface text-foreground transition-colors hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2";

export function CallDock() {
  const { phase, remoteStream, micOn, camOn, screenOn, setMic, setCam, toggleScreen, hangup } =
    useSupport();
  const { pathname } = useLocation();

  // Ref callback, non useEffect+useRef: finche' il dock e' nascosto il <video> non
  // esiste, e un effetto con deps [remoteStream] non ripartirebbe alla comparsa del
  // nodo se lo stream nel frattempo non e' cambiato — l'anteprima restava nera per
  // sempre. La ref callback viene invocata quando il nodo entra nel DOM e ogni volta
  // che lo stream cambia, che sono esattamente i due momenti in cui va agganciato.
  const agganciaVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      if (el) el.srcObject = remoteStream;
    },
    [remoteStream],
  );

  // Su /supporto c'e' gia' CallView: due viste della stessa chiamata, con gli stessi
  // controlli duplicati, sono solo confusione.
  if (phase !== "connected" || pathname.startsWith("/supporto")) return null;

  return (
    <div
      role="region"
      aria-label="Chiamata di supporto in corso"
      // Angolo in basso a SINISTRA (a destra c'e' la bolla della chat, bottom-4 right-4
      // z-50): due elementi fissi sovrapposti si coprirebbero a vicenda. Su mobile
      // `right-20` lascia libera la bolla. z-40 = sotto i modali (z-50), sopra il resto.
      className="fixed bottom-4 left-4 right-20 z-40 flex items-center gap-3 rounded-xl bg-card p-3 shadow-2xl ring-1 ring-black/10 sm:right-auto md:left-[236px]"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <video
        ref={agganciaVideo}
        autoPlay
        playsInline
        muted
        aria-hidden="true"
        className="hidden h-14 w-20 flex-shrink-0 rounded-lg bg-black object-cover sm:block"
      />
      {/* Sotto sm il testo verrebbe schiacciato dai controlli ("S...o liv..."):
          a 390px resta il pallino rosso, che dice la stessa cosa senza truncation.
          L'aria-label della region copre gli screen reader in entrambi i casi. */}
      <span
        className="ml-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-600 sm:hidden"
        aria-hidden="true"
      />
      <div className="hidden min-w-0 sm:block">
        <p className="flex items-center gap-2 text-sm font-medium">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-red-600" aria-hidden="true" />
          Supporto live
        </p>
        <p className="truncate text-xs text-muted-foreground">Sessione in corso, non registrata</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMic(!micOn)}
          aria-pressed={micOn}
          aria-label={micOn ? "Disattiva microfono" : "Attiva microfono"}
          className={iconButton}
        >
          {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4 text-red-600" />}
        </button>
        <button
          type="button"
          onClick={() => setCam(!camOn)}
          aria-pressed={camOn}
          aria-label={camOn ? "Disattiva webcam" : "Attiva webcam"}
          className={iconButton}
        >
          {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4 text-red-600" />}
        </button>
        <button
          type="button"
          onClick={() => void toggleScreen()}
          aria-pressed={screenOn}
          aria-label={screenOn ? "Interrompi condivisione schermo" : "Condividi schermo"}
          className={iconButton}
        >
          {screenOn ? <MonitorOff className="h-4 w-4 text-brand" /> : <Monitor className="h-4 w-4" />}
        </button>
        <Link
          to="/supporto"
          aria-label="Torna alla chiamata a schermo pieno"
          className="flex h-11 items-center gap-2 rounded-lg bg-surface px-3 text-sm transition-colors hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          <Maximize2 className="h-4 w-4" aria-hidden="true" />
          <span className="hidden lg:inline">Torna alla chiamata</span>
        </Link>
        <button
          type="button"
          onClick={hangup}
          aria-label="Riaggancia"
          className="flex h-11 items-center gap-2 rounded-lg bg-red-600 px-3 text-sm font-medium text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
        >
          <PhoneOff className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Riaggancia</span>
        </button>
      </div>
    </div>
  );
}
